import { Provider } from "react-redux"
import { ToastContainer } from "react-toastify"

import { ApolloProvider } from "@apollo/client"

import "scrivito-neoletter-form-widgets/index.css"
import {
  DEFAULT_TOAST_POSITION,
  DEFAULT_TOAST_TIMEOUT
} from "shared/constants/app-constants"
import { DefaultLayoutContextProvider } from "shared/context/LayoutContext"
import { PageContextProvider } from "shared/context/PageContext"
import { RuntimeContext } from "shared/context/RuntimeContext"
import { ScreenSizeContextProvider } from "shared/context/ScreenSizeContext"
import { NavigationContextProvider } from "shared/context/navigation/NavigationContext"
import { OverlayContextProvider } from "shared/context/overlay/OverlayContext"
import "shared/localization/form-validation"
import { AccessibilityInfo } from "shared/ui/accessibility-info/AccessibilityInfo"

import { apolloClient } from "app/apolloRestLink"
import { Overlays } from "app/overlays/Overlays"
import { NotificationProvider } from "app/providers/NotificationProvider"
import { Router } from "app/router/Router"
import { Routes } from "app/router/Routes"
import { store } from "app/store"
import { BaseLayout } from "app/ui/base-layout/BaseLayout"
import { useApp } from "app/useApp"

import { CustomHelmetProvider } from "CustomHelmetProvider"
import { ProjectCollaborationContextProvider } from "features/project-collaboration/model/ProjectCollaborationContextProvider"
import { AuthProvider } from "widgets/auth-provider/AuthProvider"
import { ErrorBoundary } from "widgets/error-boundary/ErrorBoundary"

import I18n from "../i18n"

function Main({
  appWrapperRef
}: {
  appWrapperRef?: React.RefCallback<HTMLElement>
}) {
  useApp()

  return (
    <ErrorBoundary>
      <div ref={appWrapperRef}>
        <Router>
          <NavigationContextProvider>
            <PageContextProvider>
              <div id="popover-root" />
              <div id="accessibility-root" className="visually-hidden" />
              <AccessibilityInfo />
              <BaseLayout>
                <Routes />
              </BaseLayout>
              <ToastContainer
                autoClose={DEFAULT_TOAST_TIMEOUT}
                position={DEFAULT_TOAST_POSITION}
              />
              <Overlays />
            </PageContextProvider>
          </NavigationContextProvider>
        </Router>
      </div>
    </ErrorBoundary>
  )
}

export function App({
  lang,
  isNode,
  appWrapperRef
}: {
  lang?: string
  isNode?: boolean
  appWrapperRef?: React.RefCallback<HTMLElement>
}) {
  if (lang) {
    I18n.changeLanguage(lang)
  }

  return (
    <RuntimeContext.Provider value={isNode ?? false}>
      <CustomHelmetProvider lang={lang}>
        <DefaultLayoutContextProvider>
          <Provider store={store}>
            <OverlayContextProvider>
              <ApolloProvider client={apolloClient}>
                <AuthProvider>
                  <ProjectCollaborationContextProvider>
                    <NotificationProvider>
                      <ScreenSizeContextProvider>
                        <Main appWrapperRef={appWrapperRef} />
                      </ScreenSizeContextProvider>
                    </NotificationProvider>
                  </ProjectCollaborationContextProvider>
                </AuthProvider>
              </ApolloProvider>
            </OverlayContextProvider>
          </Provider>
        </DefaultLayoutContextProvider>
      </CustomHelmetProvider>
    </RuntimeContext.Provider>
  )
}
